import { useEffect, useState, useCallback } from 'react';
import { Navigate } from 'react-router-dom';
import {
  makeStyles,
  tokens,
  Text,
  Input,
  Button,
  Spinner,
  MessageBar,
  MessageBarBody,
  Tag,
  Field,
  Switch,
  Toast,
  ToastTitle,
  useToastController,
} from '@fluentui/react-components';
import { AppTopBar } from '../components/AppTopBar.js';
import { Breadcrumbs } from '../components/Breadcrumbs.js';
import { FieldHelp } from '../components/FieldHelp.js';
import { GLOBAL_TOASTER_ID } from '../components/ThemedProvider.js';
import { useAuthStore } from '../stores/authStore.js';
import {
  getCalibracionVigente,
  publicarCalibracion,
  type CalibracionDoc,
  type FactorCalibracion,
} from '../api/calibracion.js';
import { ApiClientError } from '../api/client.js';
import { gateables, validarCalibracion } from './calibracionValidation.js';
import { productoActivo } from '../lib/productoActivo.js';
import {
  DESCRIPCION_PARAMETROS_CHEQUEO_PLANO,
  PARAMETROS_CHEQUEO_PLANO_DEFAULT,
  RANGO_PARAMETROS_CHEQUEO_PLANO,
  DESCRIPCION_PARAMETROS_CAMBIO_DE_PLANO,
  PARAMETROS_CAMBIO_DE_PLANO_DEFAULT,
  RANGO_PARAMETROS_CAMBIO_DE_PLANO,
  DESCRIPCION_PARAMETROS_MEDICION_DWG,
  PARAMETROS_MEDICION_DWG_DEFAULT,
  RANGO_PARAMETROS_MEDICION_DWG,
} from '@supernova/shared';

/** Etiqueta con ícono «?» de ayuda contextual (misma mecánica que el Contexto
 *  de obra; textos en la tabla global context_help_texts, editables por staff). */
function LabelHelp({ text, helpKey }: { text: string; helpKey: string }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      <span>{text}</span>
      <FieldHelp helpKey={helpKey} />
    </span>
  );
}

const useStyles = makeStyles({
  page: { display: 'flex', flexDirection: 'column', minHeight: '100vh' },
  content: { flex: 1, padding: '24px 32px', maxWidth: '1180px', width: '100%', margin: '0 auto' },
  title: { fontSize: tokens.fontSizeBase600, fontWeight: tokens.fontWeightSemibold },
  subtitle: { color: tokens.colorNeutralForeground3, marginBottom: '20px' },
  lock: { display: 'flex', alignItems: 'center', gap: '8px', color: tokens.colorNeutralForeground3, fontSize: tokens.fontSizeBase200, marginBottom: '16px' },
  section: { border: `1px solid ${tokens.colorNeutralStroke2}`, borderRadius: tokens.borderRadiusMedium, background: tokens.colorNeutralBackground1, padding: '16px 20px', marginBottom: '16px' },
  sectionTitle: { fontSize: tokens.fontSizeBase400, fontWeight: tokens.fontWeightSemibold, marginBottom: '4px' },
  sectionNote: { color: tokens.colorNeutralForeground3, fontSize: tokens.fontSizeBase200, marginBottom: '12px' },
  fieldGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '16px 20px', alignItems: 'start', maxWidth: '760px' },
  fullInput: { width: '100%' },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: { textAlign: 'left', padding: '8px 10px', background: tokens.colorNeutralBackground2, borderBottom: `1px solid ${tokens.colorNeutralStroke2}`, fontWeight: tokens.fontWeightSemibold, fontSize: tokens.fontSizeBase200, color: tokens.colorNeutralForeground3, whiteSpace: 'nowrap' },
  td: { padding: '7px 10px', borderBottom: `1px solid ${tokens.colorNeutralStroke3}`, fontSize: tokens.fontSizeBase200, verticalAlign: 'middle' },
  numInput: { width: '76px' },
  mut: { color: tokens.colorNeutralForeground3 },
  footer: { display: 'flex', justifyContent: 'flex-end', gap: '12px', alignItems: 'center', marginTop: '8px' },
  fnote: { marginRight: 'auto', color: tokens.colorNeutralForeground3, fontSize: tokens.fontSizeBase200 },
});

export function StaffCalibracionPage() {
  const styles = useStyles();
  // El panel muestra lo común y lo del producto por el que se entró (ai.* o ci.*). La calibración es
  // UNA: al publicar se conservan también los valores del otro producto (van en el mismo documento).
  const producto = productoActivo();
  const tenant = useAuthStore((s) => s.tenant);
  const isStaff = !!tenant?.isEcosoftOwned && (tenant.role === 'admin' || tenant.role === 'owner');
  const { dispatchToast } = useToastController(GLOBAL_TOASTER_ID);

  const [doc, setDoc] = useState<CalibracionDoc | null>(null);
  const [version, setVersion] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const v = await getCalibracionVigente();
      setDoc(v.calibracion);
      setVersion(v.version);
    } catch (e) {
      setError(e instanceof ApiClientError ? e.message : 'No se pudo cargar la calibración');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isStaff) void load();
  }, [isStaff, load]);

  function setTope(pct: number) {
    setDoc((d) => (d ? { ...d, combinacion: { ...d.combinacion, tope: pct / 100 } } : d));
  }
  function setUmbral(key: keyof CalibracionDoc['umbrales'], val: number) {
    setDoc((d) => (d ? { ...d, umbrales: { ...d.umbrales, [key]: val } } : d));
  }
  function setFactorAjuste(id: string, field: 'default_pct' | 'rango_min_pct' | 'rango_max_pct', val: number) {
    setDoc((d) => {
      if (!d) return d;
      const apply = (arr?: FactorCalibracion[]) =>
        (arr ?? []).map((f) =>
          f.id === id ? { ...f, ajuste: { ...(f.ajuste ?? {}), [field]: val } } : f,
        );
      return {
        ...d,
        factores: {
          ...d.factores,
          nivel_proyecto: d.factores.nivel_proyecto
            ? { ...d.factores.nivel_proyecto, factores: apply(d.factores.nivel_proyecto.factores) }
            : d.factores.nivel_proyecto,
          nivel_concepto: d.factores.nivel_concepto
            ? { ...d.factores.nivel_concepto, factores_rendimiento: apply(d.factores.nivel_concepto.factores_rendimiento) }
            : d.factores.nivel_concepto,
        },
      };
    });
  }

  function setFactorActivo(id: string, activo: boolean) {
    setDoc((d) => {
      if (!d) return d;
      const apply = (arr?: FactorCalibracion[]) => (arr ?? []).map((f) => (f.id === id ? { ...f, activo } : f));
      return {
        ...d,
        factores: {
          ...d.factores,
          nivel_proyecto: d.factores.nivel_proyecto
            ? { ...d.factores.nivel_proyecto, factores: apply(d.factores.nivel_proyecto.factores) }
            : d.factores.nivel_proyecto,
          nivel_concepto: d.factores.nivel_concepto
            ? { ...d.factores.nivel_concepto, factores_rendimiento: apply(d.factores.nivel_concepto.factores_rendimiento) }
            : d.factores.nivel_concepto,
        },
      };
    });
  }

  const valErrors = doc ? validarCalibracion(doc) : [];

  async function onPublish() {
    if (!doc || valErrors.length > 0) return;
    setSaving(true);
    try {
      const res = await publicarCalibracion(doc);
      dispatchToast(
        <Toast><ToastTitle>Versión {res.version} publicada</ToastTitle></Toast>,
        { intent: 'success', timeout: 4000 },
      );
      await load();
    } catch (e) {
      dispatchToast(
        <Toast><ToastTitle>{e instanceof ApiClientError ? e.message : 'No se pudo publicar'}</ToastTitle></Toast>,
        { intent: 'error', timeout: 6000 },
      );
    } finally {
      setSaving(false);
    }
  }

  if (!isStaff) return <Navigate to="/" replace />;

  return (
    <div className={styles.page}>
      <AppTopBar />
      <Breadcrumbs
        items={[
          { label: 'Inicio', to: '/projects' },
          { label: 'Staff' },
          { label: 'Calibración del motor' },
        ]}
      />
      <div className={styles.content}>
        <Text as="h1" className={styles.title}>Calibración del motor APU-IA</Text>
        <div className={styles.subtitle}>Motor {version ? `v${version}` : ''} · solo Ecosoft. El cotizador no ve esta pantalla.</div>
        <div className={styles.lock}>🔒 Lo que cambies aquí aplica a los proyectos nuevos. Afínalo en el entorno de pruebas antes de producción.</div>

        {loading && <Spinner label="Cargando calibración…" />}
        {error && !loading && (
          <MessageBar intent="error"><MessageBarBody>{error}</MessageBarBody></MessageBar>
        )}

        {doc && !loading && (
          <>
            <div className={styles.section}>
              <div className={styles.sectionTitle}>Regla de combinación <span className={styles.sectionNote}>· común a OPUS ai y OPUS ci</span></div>
              <div className={styles.sectionNote}>Composición multiplicativa. El tope acota el castigo total por componente (mano de obra / equipo).</div>
              <div className={styles.fieldGrid}>
                <Field label={<LabelHelp text="Método" helpKey="calib.metodo" />}>
                  <Input className={styles.fullInput} value={doc.combinacion.metodo} disabled />
                </Field>
                <Field label={<LabelHelp text="Tope máximo (%)" helpKey="calib.tope" />} hint="Negativo. Ej. −45">
                  <Input
                    className={styles.fullInput}
                    type="number"
                    value={String(Math.round(doc.combinacion.tope * 100))}
                    onChange={(_e, d) => setTope(Number(d.value) || 0)}
                  />
                </Field>
              </div>
            </div>

            <div className={styles.section}>
              <div className={styles.sectionTitle}>Análisis con IA y precios <span className={styles.sectionNote}>· común a OPUS ai y OPUS ci</span></div>
              <div className={styles.sectionNote}>Semáforo del análisis con IA (0 a 1) y cómo se consiguen y vigilan los precios. Aplica igual en los dos productos.</div>
              <div className={styles.fieldGrid}>
                <Field label={<LabelHelp text="APU verde: cobertura ≥" helpKey="calib.apu_cobertura_verde_min" />} hint="APU confiable si tantos insumos hay en catálogo">
                  <Input className={styles.fullInput} type="number" step="0.01" value={String(doc.umbrales.apu_cobertura_verde_min)} onChange={(_e, d) => setUmbral('apu_cobertura_verde_min', Number(d.value) || 0)} />
                </Field>
                <Field label={<LabelHelp text="APU rojo: cobertura <" helpKey="calib.apu_cobertura_roja_max" />} hint="APU dudoso si la cobertura cae bajo esto">
                  <Input className={styles.fullInput} type="number" step="0.01" value={String(doc.umbrales.apu_cobertura_roja_max)} onChange={(_e, d) => setUmbral('apu_cobertura_roja_max', Number(d.value) || 0)} />
                </Field>
                <Field label={<LabelHelp text="APU rojo: reducción ≥" helpKey="calib.apu_reduccion_roja_min" />} hint="Fracción 0–1. Ej. 0.40 = los factores bajan el rendimiento ≥40%">
                  <Input className={styles.fullInput} type="number" step="0.01" value={String(doc.umbrales.apu_reduccion_roja_min)} onChange={(_e, d) => setUmbral('apu_reduccion_roja_min', Number(d.value) || 0)} />
                </Field>
                <Field label={<LabelHelp text="APU amarillo: reducción ≥" helpKey="calib.apu_reduccion_amarilla_min" />} hint="Fracción 0–1. Ej. 0.20 = reducción ≥20%">
                  <Input className={styles.fullInput} type="number" step="0.01" value={String(doc.umbrales.apu_reduccion_amarilla_min)} onChange={(_e, d) => setUmbral('apu_reduccion_amarilla_min', Number(d.value) || 0)} />
                </Field>
                <Field label={<LabelHelp text="Caché de precios (días)" helpKey="calib.market_price_cache_ttl_days" />} hint="Reusa un precio consultado hace menos de N días. 0 = desactivado">
                  <Input className={styles.fullInput} type="number" step="1" min={0} value={String(doc.umbrales.market_price_cache_ttl_days)} onChange={(_e, d) => setUmbral('market_price_cache_ttl_days', Math.round(Number(d.value)) || 0)} />
                </Field>
                <Field label={<LabelHelp text="Tipo de cambio (horas)" helpKey="calib.fx_rate_ttl_hours" />} hint="Reusa un tipo de cambio consultado hace menos de N horas. 0 = consultar siempre">
                  <Input className={styles.fullInput} type="number" step="1" min={0} value={String(doc.umbrales.fx_rate_ttl_hours)} onChange={(_e, d) => setUmbral('fx_rate_ttl_hours', Math.round(Number(d.value)) || 0)} />
                </Field>
                <Field label={<LabelHelp text="Cordura de precios (veces)" helpKey="calib.precio_cordura_factor" />} hint="Descarta el precio que supere N veces el insumo más caro del mismo tipo y unidad en el catálogo del cliente. 0 = desactivado">
                  <Input className={styles.fullInput} type="number" step="0.5" min={0} value={String(doc.umbrales.precio_cordura_factor)} onChange={(_e, d) => setUmbral('precio_cordura_factor', Number(d.value) || 0)} />
                </Field>
                <Field label={<LabelHelp text="Piso de precios (veces)" helpKey="calib.precio_piso_factor" />} hint="Marca el precio que quede N veces por debajo del insumo más barato del mismo tipo y unidad en el catálogo del cliente. 1 = el propio insumo más barato. El precio nunca queda en cero: se vuelve a resolver. 0 = desactivado">
                  <Input className={styles.fullInput} type="number" step="0.5" min={0} value={String(doc.umbrales.precio_piso_factor)} onChange={(_e, d) => setUmbral('precio_piso_factor', Number(d.value) || 0)} />
                </Field>
              </div>
            </div>

            {producto === 'ai' && (
            <div className={styles.section}>
              <div className={styles.sectionTitle}>Equivalentes y lectura del listado <span className={styles.sectionNote}>· OPUS ai</span></div>
              <div className={styles.sectionNote}>Cortes de match del concepto contra el catálogo (origen del precio) y la lectura del listado de conceptos de la licitación.</div>
              <div className={styles.fieldGrid}>
                <Field label={<LabelHelp text="Match verde ≥" helpKey="calib.match_verde_min" />} hint="Toma precio directo del catálogo">
                  <Input className={styles.fullInput} type="number" step="0.01" value={String(doc.umbrales.match_verde_min)} onChange={(_e, d) => setUmbral('match_verde_min', Number(d.value) || 0)} />
                </Field>
                <Field label={<LabelHelp text="Match rojo <" helpKey="calib.match_rojo_max" />} hint="Manda a análisis con IA">
                  <Input className={styles.fullInput} type="number" step="0.01" value={String(doc.umbrales.match_rojo_max)} onChange={(_e, d) => setUmbral('match_rojo_max', Number(d.value) || 0)} />
                </Field>
                <Field label={<LabelHelp text="Correspondencia mínima <" helpKey="calib.match_gate_min" />} hint="Sin vocabulario discriminativo compartido con ningún candidato → 'no encontrado' sin gastar IA. 0 = apagado">
                  <Input className={styles.fullInput} type="number" step="0.01" value={String(doc.umbrales.match_gate_min)} onChange={(_e, d) => setUmbral('match_gate_min', Number(d.value) || 0)} />
                </Field>
                <Field label={<LabelHelp text="Tolerancia por página (renglones)" helpKey="calib.reconciliacion_tolerancia_renglones" />} hint="Cuántos renglones de diferencia se le toleran a una página del catálogo antes de volver a leerla. 0 = cualquier diferencia">
                  <Input className={styles.fullInput} type="number" step="1" min={0} value={String(doc.umbrales.reconciliacion_tolerancia_renglones)} onChange={(_e, d) => setUmbral('reconciliacion_tolerancia_renglones', Math.max(0, Math.round(Number(d.value)) || 0))} />
                </Field>
                <Field label={<LabelHelp text="Tope de relecturas (páginas)" helpKey="calib.reconciliacion_max_relecturas" />} hint="Cuántas páginas como máximo se vuelven a leer por documento. 0 = sin tope. El informe siempre dice cuántas de cuántas se releyeron">
                  <Input className={styles.fullInput} type="number" step="1" min={0} value={String(doc.umbrales.reconciliacion_max_relecturas)} onChange={(_e, d) => setUmbral('reconciliacion_max_relecturas', Math.max(0, Math.round(Number(d.value)) || 0))} />
                </Field>
                <Field label={<LabelHelp text="Exigir que el listado cierre" helpKey="calib.reconciliacion_exigir_cierre" />} hint="0 (default) = el presupuesto se entrega y el renglón que no se pudo leer entra a la hoja marcado y sin precio, con aviso al usuario. 1 = el presupuesto no se entrega">
                  <Input className={styles.fullInput} type="number" step="1" min={0} max={1} value={String(doc.umbrales.reconciliacion_exigir_cierre)} onChange={(_e, d) => setUmbral('reconciliacion_exigir_cierre', Number(d.value) === 1 ? 1 : 0)} />
                </Field>
              </div>
            </div>
            )}

            {producto === 'ci' && (
            <div className={styles.section}>
              <div className={styles.sectionTitle}>Especificaciones de las láminas de detalle <span className={styles.sectionNote}>· OPUS ci</span></div>
              <div className={styles.sectionNote}>Cuándo un texto de una lámina «Solo referencia» se pega a un concepto del presupuesto por el parecido de su descripción (0 a 1). Si el detalle y el concepto comparten código de llamada, se pegan siempre.</div>
              <div className={styles.fieldGrid}>
                <Field label={<LabelHelp text="Confianza alta: parecido ≥" helpKey="calib.spec_vinculo_alta_min" />} hint="Entre 0 y 1. Default 0.5">
                  <Input className={styles.fullInput} type="number" step="0.01" min={0} max={1} value={String(doc.umbrales.spec_vinculo_alta_min)} onChange={(_e, d) => setUmbral('spec_vinculo_alta_min', Number(d.value) || 0)} />
                </Field>
                <Field label={<LabelHelp text="Confianza alta: ventaja sobre el 2º ≥" helpKey="calib.spec_vinculo_alta_margen" />} hint="Entre 0 y 1. Default 0.1">
                  <Input className={styles.fullInput} type="number" step="0.01" min={0} max={1} value={String(doc.umbrales.spec_vinculo_alta_margen)} onChange={(_e, d) => setUmbral('spec_vinculo_alta_margen', Number(d.value) || 0)} />
                </Field>
                <Field label={<LabelHelp text="Confianza media: parecido ≥" helpKey="calib.spec_vinculo_media_min" />} hint="Entre 0 y 1. Default 0.34. Por debajo no se pega">
                  <Input className={styles.fullInput} type="number" step="0.01" min={0} max={1} value={String(doc.umbrales.spec_vinculo_media_min)} onChange={(_e, d) => setUmbral('spec_vinculo_media_min', Number(d.value) || 0)} />
                </Field>
                <Field label={<LabelHelp text="Mismo elemento con otra palabra: parecido ≥" helpKey="calib.spec_vinculo_mismo_elemento_min" />} hint="Entre 0 y 1. Default 0.7. 1 = apagado">
                  <Input className={styles.fullInput} type="number" step="0.01" min={0} max={1} value={String(doc.umbrales.spec_vinculo_mismo_elemento_min ?? 0.7)} onChange={(_e, d) => setUmbral('spec_vinculo_mismo_elemento_min', Number(d.value) || 0)} />
                </Field>
                <Field label={<LabelHelp text="«Proponer más»: misma necesidad si se parece ≥" helpKey="calib.necesidad_repetida_min" />} hint="Entre 0 y 1. Default 0.55. 1 = no se descarta nada">
                  <Input className={styles.fullInput} type="number" step="0.01" min={0} max={1} value={String(doc.umbrales.necesidad_repetida_min ?? 0.55)} onChange={(_e, d) => setUmbral('necesidad_repetida_min', Number(d.value) || 0)} />
                </Field>
                <Field label="Evidencia de la IA: largo mínimo de la cita (caracteres)" hint="Entero entre 1 y 200. Default 12. La IA debe copiar una frase literal; una cita más corta no cuenta">
                  <Input className={styles.fullInput} type="number" step="1" min={1} max={200} value={String(doc.umbrales.cita_min_caracteres ?? 12)} onChange={(_e, d) => setUmbral('cita_min_caracteres', Math.round(Number(d.value)) || 1)} />
                </Field>
                <Field label="Evidencia de la IA: palabra en común con el trabajo (letras)" hint="Entero entre 2 y 12. Default 5. La cita debe compartir una palabra de este largo con el trabajo">
                  <Input className={styles.fullInput} type="number" step="1" min={2} max={12} value={String(doc.umbrales.cita_min_letras_palabra ?? 5)} onChange={(_e, d) => setUmbral('cita_min_letras_palabra', Math.round(Number(d.value)) || 2)} />
                </Field>
              </div>
            </div>
            )}

            {producto === 'ci' && (<>
            {/* Medición de modelos BIM (IFC) en el visor de planos: cuándo una cantidad que el modelo
                declara "mide lo mismo" que su geometría. Defaults en @supernova/shared. */}
            <div className={styles.section}>
              <div className={styles.sectionTitle}>Medición de modelos BIM (IFC) <span className={styles.sectionNote}>· OPUS ci</span></div>
              <div className={styles.sectionNote}>
                Cuándo se acepta una cantidad que declara el modelo: tiene que medir lo mismo que su geometría, con esta tolerancia (en veces). Calibrado contra FZK-Haus. El peso de una pieza sale del peso que declara el modelo; si no, de su volumen por la densidad de su material (la del modelo o, si no la trae, la de su familia); si no se sabe de qué material es, queda sin pesar.
              </div>
              <div className={styles.fieldGrid}>
                <Field label={<LabelHelp text="Tolerancia de área (veces)" helpKey="calib.ifc_ventana_area" />} hint="Entre 1 y 10. Default 1,7: separa la cara de un muro de su huella y acepta el vano de una ventana contra su marco">
                  <Input className={styles.fullInput} type="number" step="0.05" min={1} max={10} value={String(doc.umbrales.ifc_ventana_area)} onChange={(_e, d) => setUmbral('ifc_ventana_area', Number(d.value) || 0)} />
                </Field>
                <Field label={<LabelHelp text="Tolerancia de largo (veces)" helpKey="calib.ifc_ventana_longitud" />} hint="Entre 1 y 10. Default 1,25: separa el largo de una losa del perímetro de su vano">
                  <Input className={styles.fullInput} type="number" step="0.05" min={1} max={10} value={String(doc.umbrales.ifc_ventana_longitud)} onChange={(_e, d) => setUmbral('ifc_ventana_longitud', Number(d.value) || 0)} />
                </Field>
                <Field label={<LabelHelp text="Tolerancia de volumen (veces)" helpKey="calib.ifc_ventana_volumen" />} hint="Entre 1 y 10. Default 1,25: neto y bruto con vanos difieren ~15%">
                  <Input className={styles.fullInput} type="number" step="0.05" min={1} max={10} value={String(doc.umbrales.ifc_ventana_volumen)} onChange={(_e, d) => setUmbral('ifc_ventana_volumen', Number(d.value) || 0)} />
                </Field>
                <Field label={<LabelHelp text="Cara plana: fracción mínima" helpKey="calib.ifc_cobertura_cara_plana" />} hint="Fracción 0–1. Default 0,1: por debajo, la pieza se trata como curva">
                  <Input className={styles.fullInput} type="number" step="0.01" min={0} max={1} value={String(doc.umbrales.ifc_cobertura_cara_plana)} onChange={(_e, d) => setUmbral('ifc_cobertura_cara_plana', Number(d.value) || 0)} />
                </Field>
                <Field label={<LabelHelp text="Tolerancia de peso (veces)" helpKey="calib.ifc_ventana_peso" />} hint="Entre 1 y 10. Default 1,25: un peso declarado se usa si cuadra con volumen × densidad">
                  <Input className={styles.fullInput} type="number" step="0.05" min={1} max={10} value={String(doc.umbrales.ifc_ventana_peso)} onChange={(_e, d) => setUmbral('ifc_ventana_peso', Number(d.value) || 0)} />
                </Field>
                <Field label={<LabelHelp text="Densidad del acero (kg/m³)" helpKey="calib.ifc_densidad_acero" />} hint="Entre 7.000 y 8.500. Default 7.850. Solo si el modelo no declara peso ni densidad">
                  <Input className={styles.fullInput} type="number" step="10" min={7000} max={8500} value={String(doc.umbrales.ifc_densidad_acero)} onChange={(_e, d) => setUmbral('ifc_densidad_acero', Number(d.value) || 0)} />
                </Field>
                <Field label={<LabelHelp text="Densidad del concreto (kg/m³)" helpKey="calib.ifc_densidad_concreto" />} hint="Entre 1.000 y 3.000. Default 2.400. Solo si el modelo no declara peso ni densidad">
                  <Input className={styles.fullInput} type="number" step="10" min={1000} max={3000} value={String(doc.umbrales.ifc_densidad_concreto)} onChange={(_e, d) => setUmbral('ifc_densidad_concreto', Number(d.value) || 0)} />
                </Field>
                <Field label={<LabelHelp text="Densidad del concreto ligero (kg/m³)" helpKey="calib.ifc_densidad_concreto_ligero" />} hint="Entre 300 y 2.000. Default 1.600. Solo si el modelo no declara peso ni densidad">
                  <Input className={styles.fullInput} type="number" step="10" min={300} max={2000} value={String(doc.umbrales.ifc_densidad_concreto_ligero)} onChange={(_e, d) => setUmbral('ifc_densidad_concreto_ligero', Number(d.value) || 0)} />
                </Field>
                <Field label={<LabelHelp text="Densidad de la madera (kg/m³)" helpKey="calib.ifc_densidad_madera" />} hint="Entre 200 y 1.300. Default 500. Solo si el modelo no declara peso ni densidad">
                  <Input className={styles.fullInput} type="number" step="10" min={200} max={1300} value={String(doc.umbrales.ifc_densidad_madera)} onChange={(_e, d) => setUmbral('ifc_densidad_madera', Number(d.value) || 0)} />
                </Field>
                <Field label={<LabelHelp text="Densidad del aluminio (kg/m³)" helpKey="calib.ifc_densidad_aluminio" />} hint="Entre 2.500 y 3.000. Default 2.700. Solo si el modelo no declara peso ni densidad">
                  <Input className={styles.fullInput} type="number" step="10" min={2500} max={3000} value={String(doc.umbrales.ifc_densidad_aluminio)} onChange={(_e, d) => setUmbral('ifc_densidad_aluminio', Number(d.value) || 0)} />
                </Field>
                <Field label={<LabelHelp text="Densidad del vidrio (kg/m³)" helpKey="calib.ifc_densidad_vidrio" />} hint="Entre 2.200 y 2.800. Default 2.500 (provisional). Solo si el modelo no declara peso ni densidad">
                  <Input className={styles.fullInput} type="number" step="10" min={2200} max={2800} value={String(doc.umbrales.ifc_densidad_vidrio)} onChange={(_e, d) => setUmbral('ifc_densidad_vidrio', Number(d.value) || 0)} />
                </Field>
                <Field label={<LabelHelp text="Densidad de la mampostería (kg/m³)" helpKey="calib.ifc_densidad_mamposteria" />} hint="Entre 500 y 2.600. Default 1.600 (provisional). Solo si el modelo no declara peso ni densidad">
                  <Input className={styles.fullInput} type="number" step="10" min={500} max={2600} value={String(doc.umbrales.ifc_densidad_mamposteria)} onChange={(_e, d) => setUmbral('ifc_densidad_mamposteria', Number(d.value) || 0)} />
                </Field>
                <Field label={<LabelHelp text="Densidad del yeso (kg/m³)" helpKey="calib.ifc_densidad_yeso" />} hint="Entre 400 y 1.600. Default 1.000 (provisional). Solo si el modelo no declara peso ni densidad">
                  <Input className={styles.fullInput} type="number" step="10" min={400} max={1600} value={String(doc.umbrales.ifc_densidad_yeso)} onChange={(_e, d) => setUmbral('ifc_densidad_yeso', Number(d.value) || 0)} />
                </Field>
                <Field label={<LabelHelp text="IFC grande: avisar desde (MB)" helpKey="calib.ifc_aviso_tamano_mb" />} hint="Entre 10 y 2.000. Default 120 (provisional): desde este tamaño se pregunta antes de abrir el modelo en el navegador">
                  <Input className={styles.fullInput} type="number" step="10" min={10} max={2000} value={String(doc.umbrales.ifc_aviso_tamano_mb)} onChange={(_e, d) => setUmbral('ifc_aviso_tamano_mb', Number(d.value) || 0)} />
                </Field>
                <Field label={<LabelHelp text="Medir por largo: largo ÷ ancho mínimo" helpKey="calib.ifc_forma_lineal_min" />} hint="Entre 1,5 y 50. Default 4: una pieza así de alargada se mide en metros">
                  <Input className={styles.fullInput} type="number" step="0.5" min={1.5} max={50} value={String(doc.umbrales.ifc_forma_lineal_min)} onChange={(_e, d) => setUmbral('ifc_forma_lineal_min', Number(d.value) || 0)} />
                </Field>
                <Field label={<LabelHelp text="Medir por área: ancho ÷ espesor mínimo" helpKey="calib.ifc_forma_plana_min" />} hint="Entre 1,5 y 100. Default 6: una pieza así de delgada se mide en m²; si no, en m³">
                  <Input className={styles.fullInput} type="number" step="0.5" min={1.5} max={100} value={String(doc.umbrales.ifc_forma_plana_min)} onChange={(_e, d) => setUmbral('ifc_forma_plana_min', Number(d.value) || 0)} />
                </Field>
                <Field label={<LabelHelp text="Niveles deducidos: tolerancia (m)" helpKey="calib.ifc_nivel_tolerancia" />} hint="Entre 0,01 y 2. Default 0,5: losas de piso a menos de esto son el mismo nivel (solo en modelos sin pisos)">
                  <Input className={styles.fullInput} type="number" step="0.05" min={0.01} max={2} value={String(doc.umbrales.ifc_nivel_tolerancia)} onChange={(_e, d) => setUmbral('ifc_nivel_tolerancia', Number(d.value) || 0)} />
                </Field>
              </div>
            </div>

            {/* Medición de planos DWG/DXF (lector del worker). Etiquetas, rangos y defaults en
                @supernova/shared (parametrosMedicionDwg.ts): el panel no escribe ningún número. */}
            <div className={styles.section}>
              <div className={styles.sectionTitle}>Medición de planos DWG/DXF <span className={styles.sectionNote}>· OPUS ci</span></div>
              <div className={styles.sectionNote}>
                Todo en metros: el lector los pasa a la unidad del dibujo, así un plano en metros, centímetros o milímetros mide lo mismo. Los rangos de tamaño deciden en qué unidad está un plano: una unidad con la que el dibujo no es una obra plausible se descarta; si quedan dos, se le pregunta al usuario. Calibrado contra la vara DWG (pnpm test:vara-dwg) y cs_05.
              </div>
              <div className={styles.fieldGrid}>
                {(Object.keys(DESCRIPCION_PARAMETROS_MEDICION_DWG) as (keyof typeof DESCRIPCION_PARAMETROS_MEDICION_DWG)[]).map((k) => {
                  const d = DESCRIPCION_PARAMETROS_MEDICION_DWG[k];
                  const [mn, mx] = RANGO_PARAMETROS_MEDICION_DWG[k];
                  return (
                    <Field key={k} label={<LabelHelp text={d.etiqueta} helpKey={`calib.${k}`} />} hint={`Entre ${mn} y ${mx}. Default ${PARAMETROS_MEDICION_DWG_DEFAULT[k]}. ${d.pista}`}>
                      <Input className={styles.fullInput} type="number" step={String(d.paso)} min={mn} max={mx} value={String(doc.umbrales[k])} onChange={(_e, v) => setUmbral(k, Number(v.value) || 0)} />
                    </Field>
                  );
                })}
              </div>
            </div>

            {/* «¿Este plano sirve para presupuestar?» (idea 10). Etiquetas, rangos y defaults en
                @supernova/shared (chequeoDelPlano.ts): el panel no escribe ningún número. */}
            <div className={styles.section}>
              <div className={styles.sectionTitle}>¿Este plano sirve para presupuestar? <span className={styles.sectionNote}>· OPUS ci</span></div>
              <div className={styles.sectionNote}>
                El chequeo que ve el usuario al abrir un plano. Todo en porcentaje. Defaults PROVISIONALES (los del demo para dirección): en un IFC, «lista» es la pieza que trae sus cantidades, dice su tipo y su piso, y no es genérica.
              </div>
              <div className={styles.fieldGrid}>
                {(Object.keys(DESCRIPCION_PARAMETROS_CHEQUEO_PLANO) as (keyof typeof DESCRIPCION_PARAMETROS_CHEQUEO_PLANO)[]).map((k) => {
                  const d = DESCRIPCION_PARAMETROS_CHEQUEO_PLANO[k];
                  const [mn, mx] = RANGO_PARAMETROS_CHEQUEO_PLANO[k];
                  return (
                    <Field key={k} label={<LabelHelp text={d.etiqueta} helpKey={`calib.${k}`} />} hint={`Entre ${mn} y ${mx}. Default ${PARAMETROS_CHEQUEO_PLANO_DEFAULT[k]}. ${d.pista}`}>
                      <Input className={styles.fullInput} type="number" step="1" min={mn} max={mx} value={String(doc.umbrales[k])} onChange={(_e, v) => setUmbral(k, Number(v.value) || 0)} />
                    </Field>
                  );
                })}
              </div>
            </div>

            {/* «Cambió el plano» (comparar dos versiones de un plano). Etiquetas, rangos y defaults en
                @supernova/shared (cambioDePlano.ts): el panel no escribe ningún número. */}
            <div className={styles.section}>
              <div className={styles.sectionTitle}>Cambió el plano <span className={styles.sectionNote}>· OPUS ci</span></div>
              <div className={styles.sectionNote}>
                Cómo se decide que una pieza cambió entre dos versiones de un plano. Cada campo dice su unidad (% o metros). Defaults PROVISIONALES.
              </div>
              <div className={styles.fieldGrid}>
                {(Object.keys(DESCRIPCION_PARAMETROS_CAMBIO_DE_PLANO) as (keyof typeof DESCRIPCION_PARAMETROS_CAMBIO_DE_PLANO)[]).map((k) => {
                  const d = DESCRIPCION_PARAMETROS_CAMBIO_DE_PLANO[k];
                  const [mn, mx] = RANGO_PARAMETROS_CAMBIO_DE_PLANO[k];
                  return (
                    <Field key={k} label={<LabelHelp text={d.etiqueta} helpKey={`calib.${k}`} />} hint={`Entre ${mn} y ${mx} ${d.unidad}. Default ${PARAMETROS_CAMBIO_DE_PLANO_DEFAULT[k]} ${d.unidad}. ${d.pista}`}>
                      <Input className={styles.fullInput} type="number" step={String(d.paso)} min={mn} max={mx} value={String(doc.umbrales[k])} onChange={(_e, v) => setUmbral(k, Number(v.value) || 0)} />
                    </Field>
                  );
                })}
              </div>
            </div>

            </>)}

            <div className={styles.section}>
              <div className={styles.sectionTitle}>Catálogo de factores <span className={styles.sectionNote}>· común a OPUS ai y OPUS ci</span></div>
              <div className={styles.sectionNote}>Magnitudes editables (default y rango, en %). Disciplina y "pega en" se editan en una versión posterior.</div>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th className={styles.th}>Factor</th>
                    <th className={styles.th}>Disciplina</th>
                    <th className={styles.th}>Pega en</th>
                    <th className={styles.th}>Default %</th>
                    <th className={styles.th}>Rango min</th>
                    <th className={styles.th}>Rango max</th>
                    <th className={styles.th}>Estado</th>
                    <th className={styles.th}></th>
                  </tr>
                </thead>
                <tbody>
                  {gateables(doc).map((f) => (
                    <tr key={f.id}>
                      <td className={styles.td}>
                        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                          <span>{f.nombre}</span>
                          <FieldHelp helpKey={`calib.factor.${f.id}`} />
                        </div>
                        <div className={styles.mut}>{f.id}</div>
                      </td>
                      <td className={styles.td}><span className={styles.mut}>{(f.disciplinas ?? ['T']).join('/')}</span></td>
                      <td className={styles.td}><span className={styles.mut}>{(f.pega_en ?? ['MO', 'EQ']).join('+')}</span></td>
                      <td className={styles.td}>
                        {f.formula ? <span className={styles.mut}>fórmula</span> : (
                          <Input className={styles.numInput} type="number" value={String(f.ajuste?.default_pct ?? '')} onChange={(_e, d) => setFactorAjuste(f.id, 'default_pct', Number(d.value) || 0)} />
                        )}
                      </td>
                      <td className={styles.td}>
                        {f.ajuste?.rango_min_pct != null && (
                          <Input className={styles.numInput} type="number" value={String(f.ajuste.rango_min_pct)} onChange={(_e, d) => setFactorAjuste(f.id, 'rango_min_pct', Number(d.value) || 0)} />
                        )}
                      </td>
                      <td className={styles.td}>
                        {f.ajuste?.rango_max_pct != null && (
                          <Input className={styles.numInput} type="number" value={String(f.ajuste.rango_max_pct)} onChange={(_e, d) => setFactorAjuste(f.id, 'rango_max_pct', Number(d.value) || 0)} />
                        )}
                      </td>
                      <td className={styles.td}>
                        <Switch checked={f.activo !== false} onChange={(_e, d) => setFactorActivo(f.id, d.checked)} label={f.activo !== false ? 'Activo' : 'Inactivo'} />
                      </td>
                      <td className={styles.td}>
                        {f.a_calibrar && <Tag size="extra-small" appearance="outline">a calibrar</Tag>}
                        {f.activo_default === false && <Tag size="extra-small" appearance="outline">off default</Tag>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {valErrors.length > 0 && (
              <MessageBar intent="warning">
                <MessageBarBody>
                  <strong>Revisa antes de publicar:</strong>
                  <ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>
                    {valErrors.map((e, i) => <li key={i}>{e}</li>)}
                  </ul>
                </MessageBarBody>
              </MessageBar>
            )}

            <div className={styles.footer}>
              <span className={styles.fnote}>⚠ Publicar crea una nueva versión vigente para proyectos nuevos.</span>
              <Button appearance="secondary" onClick={() => void load()} disabled={saving}>Descartar cambios</Button>
              <Button appearance="primary" onClick={() => void onPublish()} disabled={saving || valErrors.length > 0}>
                {saving ? <Spinner size="tiny" /> : 'Guardar y publicar versión'}
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
